'use client'

import { useState } from 'react'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session'

import { reloadPage } from '@marketplace-web/shared/browser'
import { clickEvent, selfServiceViewScreenEvent } from '_libs/common/event-tracker/events'

import { completeTransaction } from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { brazeLogCustomEvent } from '@marketplace-web/braze/braze-feature'
import { BrazeCustomEvent } from '@marketplace-web/braze/braze-data'
import {
  ConversationActionButton,
  ConversationActionProps,
} from '@marketplace-web/messaging/conversation-action-feature'

import ConfirmDeliveredEverythingIsOkModal from './ConfirmDeliveredEverythingIsOkModal'

const CompleteDeliveredTransaction = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: ConversationActionProps) => {
  const { track } = useTracking()
  const { user } = useSession()

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id

  const isBrazeEnabled = useFeatureSwitch('braze_sdk_enabled')
  const [isModalOpen, setIsModalOpen] = useState(false)

  function trackConfirmButtonClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: ClickableElement.ConfirmEverythingIsOk,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function trackModalScreen() {
    if (!transactionId) return

    track(
      selfServiceViewScreenEvent({
        screen: Screen.ConfirmEverythingIsOkModal,
        transactionId,
      }),
    )
  }

  function openModal() {
    trackConfirmButtonClick()
    trackModalScreen()
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
  }

  async function confirmCompleteTransaction() {
    if (!transactionId) return

    const response = await completeTransaction(transactionId)

    if ('errors' in response) return

    if (isBrazeEnabled) {
      brazeLogCustomEvent(
        {
          event: BrazeCustomEvent.EscrowPurchaseCompleted,
          modelId: transactionId,
          userExternalId: user?.external_id,
        },
        {
          defer: true,
        },
      )
    }

    reloadPage()
  }

  if (!transactionId) return null

  return (
    <>
      <ConversationActionButton
        primary={primary}
        text={title}
        size={size}
        inverse={inverse}
        theme={theme}
        onClick={openModal}
        testId="conversation-action--complete-transaction-button"
      />
      <ConfirmDeliveredEverythingIsOkModal
        isOpen={isModalOpen}
        onSubmit={confirmCompleteTransaction}
        onClose={closeModal}
      />
    </>
  )
}

export default CompleteDeliveredTransaction
